import PropTypes from 'prop-types';
import { catsearch } from '../../../textSearch';
import nodeSortingOptionsSequence from '../../../utils/nodeSortingOptionsSequence';
import makeTreePlugins from '../DynamicTree/internal/makeTreePlugins';
import selectionModes from '../DynamicTree/internal/selectionModes';
import CommonAdapterTree from './CommonAdapterTree';

class CommonDynamicTree extends CommonAdapterTree {
  static propTypes = {
    ...CommonAdapterTree.propTypes,

    permission: PropTypes.string,
    subtreeFilter: PropTypes.any,
    client: PropTypes.object,

    gqlQueries: PropTypes.shape({
      childDataGqlQuery: PropTypes.object,
      childDataGqlVariables: PropTypes.object,
      mainDataGqlQuery: PropTypes.object,
      mainDataGqlVariables: PropTypes.object,
      searchDataGqlQuery: PropTypes.object,
      searchDataGqlVariables: PropTypes.object,

      formatAfterSearchDataGqlQuery: PropTypes.func,
      formatAfterMainDataGqlQuery: PropTypes.func,
      formatMainVarsBeforeSend: PropTypes.func,
      formatSearchVarsBeforeSend: PropTypes.func,
      mainDataGqlSkip: PropTypes.bool,
    }),
    plugins: PropTypes.shape({
      addNodeBuildMeta: PropTypes.func,
      nodeIsVisible: PropTypes.func,
      nodeCanLoadChildren: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.bool,
      ]),
      nodeSortingOptions: PropTypes.shape({
        iteratees: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.func,
          PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
          ),
        ]),
        orders: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      }),
      nodeIsSelectable: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
      resolveNodePopoverText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.func,
      ]),
      resolveNodeIconName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
        PropTypes.func,
      ]),
      nodeTotalLeafCounterOptions: PropTypes.shape({
        isEnabled: PropTypes.bool,
        autoShow: PropTypes.bool,
      }),
      lazyLoadingOptions: PropTypes.shape({
        reloadSeedData: PropTypes.bool,
        nodeCanLoadChildren: PropTypes.func.isRequired,
        createObservableQuery: PropTypes.func.isRequired,
      }),
      hasDynamicLeaves: PropTypes.bool,
      nodeExpansionOptions: PropTypes.shape({
        inflateInitialExpansion: PropTypes.bool,
        nodeShouldBeInitiallyVisible: PropTypes.func,
        hasDeepExpansionCheck: PropTypes.bool,
      }),
    }),
    nodeExpansionOptions: PropTypes.shape({
      inflateInitialExpansion: PropTypes.bool,
      nodeShouldBeInitiallyVisible: PropTypes.func,
      hasDeepExpansionCheck: PropTypes.bool,
    }),
  };

  static defaultProps = {
    ...CommonAdapterTree.defaultProps,

    permission: null,
    subtreeFilter: null,
    client: undefined,
    gqlQueries: {
      mainDataGqlVariables: {},
      searchDataGqlQuery: null,
      searchDataGqlVariables: {},
      childDataGqlQuery: null,
      childDataGqlVariables: {},
      formatAfterSearchDataGqlQuery: null,
      formatAfterMainDataGqlQuery: null,
      formatMainVarsBeforeSend: null,
      formatSearchVarsBeforeSend: null,
    },
    plugins: {},
    nodeExpansionOptions: null,
  };

  nodeSortingOptions = nodeSortingOptionsSequence;
  nodeIsSelectable = true;
  hasDynamicLeaves = true;

  constructor(props) {
    super(props);

    const {
      gqlQueries: { mainDataGqlQuery },
    } = props;

    if (!mainDataGqlQuery) {
      throw new Error(
        'Main GraphQL query should be passed in Dynamic Data mode',
      );
    }
  }

  get nodeExpansionOptions() {
    const nodeExpansionOptions =
      this.props?.nodeExpansionOptions ||
      this.props.plugins?.nodeExpansionOptions;

    if (nodeExpansionOptions) {
      return nodeExpansionOptions;
    }

    const searchQuery =
      this.props.filter?.value?.searchQuery || this.state.filter?.searchQuery;

    return {
      nodeShouldBeInitiallyVisible: node =>
        searchQuery
          ? catsearch(this.resolveNodeTitle(node), searchQuery)
          : true,
      inflateInitialExpansion: !searchQuery,
    };
  }

  cleanupSearchQuery = query => (query || '').trim();

  splitSearchQuery = query =>
    (query || '')
      .replace(/[^\w\d-]+/g, ' ')
      .trim()
      .split(' ');

  prepareSearchQuery = query => {
    const cleanQuery = this.cleanupSearchQuery(query);
    const queryParts = this.splitSearchQuery(cleanQuery);
    return queryParts.join(' ');
  };

  makePlugins = () => {
    const {
      selectionMode,
      client,
      permission,
      gqlQueries: { childDataGqlQuery, childDataGqlVariables },
      t,
      plugins,
    } = this.props;

    const {
      addNodeBuildMeta,
      resolveNodePopoverText,
      resolveNodeIconName,
      nodeTotalLeafCounterOptions,
      lazyLoadingOptions,

      nodeIsVisible = this.nodeIsVisible,
      nodeCanLoadChildren = this.nodeCanLoadChildren,
      nodeSortingOptions = this.nodeSortingOptions,
      nodeIsSelectable = this.nodeIsSelectable,
      hasDynamicLeaves = this.hasDynamicLeaves,
    } = plugins;

    return makeTreePlugins(
      {
        addNodeBuildMeta,
        nodeIsVisible,
        nodeCanLoadChildren,
        nodeSortingOptions,
        nodeIsSelectable,
        lazyLoadingOptions,
        hasDynamicLeaves,
        nodeTotalLeafCounterOptions,
        resolveNodeIconName,
        resolveNodePopoverText,

        resolveNodeClassName: this.resolveNodeClassName,
        resolveNodeCustomText: this.resolveNodeCustomText,
        getNodeParentId: this.resolveNodeParentId,

        selectionMode: selectionModes[selectionMode],
        nodeExpansionOptions: this.nodeExpansionOptions,
      },
      {
        client,
        query: childDataGqlQuery,
        variables: childDataGqlVariables,
        t,
        permission,
      },
    );
  };

  nodeIsVisible = () => true;

  nodeCanLoadChildren = () => true;
}

export default CommonDynamicTree;
